import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Stack } from '@mui/material';
import { get } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

// Admin/back-office session inactivity manager.
//
// Sliding server session times out after `idleTimeoutMs` of inactivity (default
// 30 min). We show a warning modal `warnBeforeMs` before expiry (default 5 min)
// with a live countdown. Real activity keeps the session alive; hitting 0:00
// with no action logs the user out. Values are read from the keep-alive endpoint
// so the client stays in sync with the backend.
const IDLE_TIMEOUT_MS_DEFAULT = 30 * 60 * 1000; // 1,800,000
const WARN_BEFORE_MS_DEFAULT = 5 * 60 * 1000; // 300,000
// Keep-alive the server at most once per this window while the user is active.
const KEEPALIVE_MIN_INTERVAL_MS = 60 * 1000;
// Ignore repeat local activity within this window so we do not reset constantly.
const ACTIVITY_THROTTLE_MS = 5 * 1000;

const SESSION_STATUS_ENDPOINT = '/api/auth/session-status';

const formatMMSS = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mins = Math.floor(s / 60);
  const secs = String(s % 60).padStart(2, '0');
  return `${mins}:${secs}`;
};

const SessionTimeoutManager = () => {
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuth();

  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(Math.floor(WARN_BEFORE_MS_DEFAULT / 1000));

  // Config from the backend (fallbacks keep us in sync if the call ever fails).
  const idleTimeoutMsRef = useRef(IDLE_TIMEOUT_MS_DEFAULT);
  const warnBeforeMsRef = useRef(WARN_BEFORE_MS_DEFAULT);

  const warnTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const lastKeepAliveRef = useRef(0);
  const lastActivityRef = useRef(0);
  // Mirror warningOpen for reads inside stable event handlers.
  const warningOpenRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (warnTimerRef.current) {
      clearTimeout(warnTimerRef.current);
      warnTimerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const setWarning = useCallback((open) => {
    warningOpenRef.current = open;
    setWarningOpen(open);
  }, []);

  // Reuse the app's normal logout mechanism: clear state + cookie, go to login.
  const redirectToLogin = useCallback(async () => {
    clearTimers();
    setWarning(false);
    try {
      await logout();
    } catch {}
    navigate('/login', { replace: true });
  }, [clearTimers, setWarning, logout, navigate]);

  // Keep-alive the server session. Reads config + slides the session.
  // Returns true on success; on 401 (dead session) redirects to login.
  const keepAlive = useCallback(async ({ force = false } = {}) => {
    const now = Date.now();
    if (!force && now - lastKeepAliveRef.current < KEEPALIVE_MIN_INTERVAL_MS) {
      return true;
    }
    lastKeepAliveRef.current = now;
    try {
      const data = await get(SESSION_STATUS_ENDPOINT);
      if (Number.isFinite(data?.idleTimeoutMs) && data.idleTimeoutMs > 0) {
        idleTimeoutMsRef.current = data.idleTimeoutMs;
      }
      if (Number.isFinite(data?.warnBeforeMs) && data.warnBeforeMs > 0) {
        warnBeforeMsRef.current = data.warnBeforeMs;
      }
      return true;
    } catch (e) {
      if (e?.status === 401) {
        void redirectToLogin();
        return false;
      }
      // Transient/network error: keep the local timers running with last-known config.
      return false;
    }
  }, [redirectToLogin]);

  const startCountdown = useCallback(() => {
    let remaining = Math.floor(warnBeforeMsRef.current / 1000);
    setSecondsRemaining(remaining);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setSecondsRemaining(remaining);
      if (remaining <= 0) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
        void redirectToLogin();
      }
    }, 1000);
  }, [redirectToLogin]);

  // (Re)arm the idle window: warning fires `warnBeforeMs` before expiry.
  const armIdleTimer = useCallback(() => {
    clearTimers();
    setWarning(false);
    const untilWarning = Math.max(0, idleTimeoutMsRef.current - warnBeforeMsRef.current);
    warnTimerRef.current = setTimeout(() => {
      setWarning(true);
      startCountdown();
    }, untilWarning);
  }, [clearTimers, setWarning, startCountdown]);

  // Real user activity. While the modal is closed, resets the idle timer and
  // (throttled) keeps the server session alive. While the modal is open, does
  // nothing so the user must consciously click to extend.
  const handleActivity = useCallback(() => {
    if (warningOpenRef.current) return;
    const now = Date.now();
    if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) return;
    lastActivityRef.current = now;
    armIdleTimer();
    void keepAlive();
  }, [armIdleTimer, keepAlive]);

  const staySignedIn = useCallback(async () => {
    // Explicit extend: force a keep-alive to slide the server session, then reset.
    const ok = await keepAlive({ force: true });
    if (!ok) return; // 401 path already redirected.
    lastActivityRef.current = Date.now();
    armIdleTimer();
  }, [keepAlive, armIdleTimer]);

  const logoutNow = useCallback(() => {
    void redirectToLogin();
  }, [redirectToLogin]);

  // Initialize when authenticated: pull config + slide session, then arm timers.
  useEffect(() => {
    if (!isAuthenticated) {
      clearTimers();
      setWarning(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const ok = await keepAlive({ force: true });
      if (cancelled || !ok) return;
      armIdleTimer();
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, keepAlive, armIdleTimer, clearTimers, setWarning]);

  // Global activity listeners (throttled inside handleActivity).
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((evt) => document.addEventListener(evt, handleActivity, { passive: true }));
    return () => {
      events.forEach((evt) => document.removeEventListener(evt, handleActivity));
    };
  }, [isAuthenticated, handleActivity]);

  // Cleanup on unmount.
  useEffect(() => () => clearTimers(), [clearTimers]);

  if (!isAuthenticated) return null;

  return (
    <Dialog open={warningOpen} maxWidth="xs" fullWidth>
      <DialogTitle>Session about to expire</DialogTitle>
      <DialogContent>
        <Stack alignItems="center" spacing={1.5} sx={{ py: 1 }}>
          <Typography variant="body1" align="center">
            You&apos;ve been inactive. You&apos;ll be signed out in {formatMMSS(secondsRemaining)}.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center' }}>
        <Stack direction="row" spacing={2} justifyContent="center" sx={{ width: '100%' }}>
          <Button variant="outlined" color="inherit" onClick={logoutNow} size="large">
            Log out now
          </Button>
          <Button variant="contained" color="primary" onClick={staySignedIn} size="large" autoFocus>
            Stay signed in
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutManager;
